import { MenuRenderer } from '@/components/menu-renderer';
import PublicFooter from '@/components/public-footer';
import PublicHeader from '@/components/public-header';
import { LRTIcon } from '@/components/LRTLogo';
import { SocialMenuRenderer } from '@/components/social-menu-renderer';
import { useFocusTrap } from '@/hooks/use-focus-trap';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';

type UserType = 'client' | 'agent';

interface HeaderConfig {
    hideHeader?: boolean;
    hideHeaderMobile?: boolean;
    position?: 'absolute' | 'relative' | 'fixed';
    background?: 'transparent' | 'white' | 'black' | 'blur';
    textColor?: 'auto' | 'light' | 'dark';
    scrollBehavior?: 'always' | 'hide' | 'show';
    elevation?: 'none' | 'sm' | 'md' | 'lg';
    // Layout options
    logoAlignment?: 'left' | 'center';
    // Render the icon-only LR mark on mobile (<sm), full LuxRally Travel wordmark at sm+.
    // Used by elite advisor pages where the "ELITE ADVISOR" tagline badge needs the horizontal room.
    compactLogoOnMobile?: boolean;
    menuAlignment?: 'left' | 'right';
    showMenu?: boolean;
    // Main nav left-cluster controls (desktop)
    showGetStarted?: boolean; // default true — show the "Get Started" pill on the left
    showPhone?: boolean; // default true — show the agency phone number next to Get Started
    // Save Contact replaces the phone item when provided (agent pages with show_save_contact)
    saveContactHref?: string;
    saveContactFilename?: string;
    // Primary CTA options (left side)
    showCta?: boolean;
    ctaText?: string;
    ctaTextMobile?: string; // optional shorter label shown below sm breakpoint
    hideCtaMobile?: boolean; // hide the primary CTA below the md breakpoint (keeps it from crowding the logo on mobile)
    ctaAction?: 'navigate' | 'scroll' | 'custom';
    ctaUrl?: string;
    ctaScrollTargetId?: string;
    ctaCustomEventName?: string;
    ctaStyle?: 'outline' | 'filled';
    // Where to render the CTA in the nav. Defaults to 'right' (the existing behavior — CTA sits
    // alongside Search/menu). Set 'left' for landing pages that want the CTA to occupy the same
    // slot as the standard "Get Started" pill.
    ctaPosition?: 'left' | 'right';
    // Secondary CTA options (right side)
    showSecondaryCta?: boolean;
    secondaryCtaText?: string;
    secondaryCtaUrl?: string;
    // Navigation bar
    showNavBar?: boolean;
    navMenuLocation?: string;
    // Search
    showSearch?: boolean;
    // Endorsement logos
    showEndorsementLogos?: boolean;
    // Tagline
    tagline?: string;
    // Custom tagline element (overrides tagline string when provided)
    taglineNode?: React.ReactNode;
}

interface DrawerContextType {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
}

interface UserTypeContextType {
    userType: UserType;
    setUserType: (type: UserType) => void;
}

interface HeaderConfigContextType {
    headerConfig: HeaderConfig;
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);
const UserTypeContext = createContext<UserTypeContextType | undefined>(undefined);
const HeaderConfigContext = createContext<HeaderConfigContextType | undefined>(undefined);

export const useDrawer = () => {
    const context = useContext(DrawerContext);
    if (!context) {
        throw new Error('useDrawer must be used within DrawerProvider');
    }
    return context;
};

export const useUserType = () => {
    const context = useContext(UserTypeContext);
    if (!context) {
        throw new Error('useUserType must be used within UserTypeProvider');
    }
    return context;
};

export const useHeaderConfig = () => {
    const context = useContext(HeaderConfigContext);
    if (!context) {
        throw new Error('useHeaderConfig must be used within HeaderConfigProvider');
    }
    return context;
};

interface PublicHeaderFooterLayoutProps extends PropsWithChildren {
    headerConfig?: HeaderConfig;
}

// Framer-motion variants for staggered drawer items
const drawerMenuVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.06,
            delayChildren: 0.15,
        },
    },
};

const drawerItemVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.4, ease: [0.32, 0.72, 0, 1] as [number, number, number, number] },
    },
};

const drawerSocialVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { duration: 0.4, delay: 0.4 },
    },
};

export default function PublicHeaderFooterLayout({ children, headerConfig = {} }: PublicHeaderFooterLayoutProps) {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [userType, setUserType] = useState<UserType>('client');
    const drawerRef = useRef<HTMLElement>(null);

    // iOS Safari top status bar color is controlled by <meta name="theme-color">
    // in app.blade.php (#0E2727). The bottom toolbar inherits from the page's
    // natural body/html background, giving it a transparent/content-matched look.

    // Focus trap for accessibility
    useFocusTrap(drawerRef, isDrawerOpen);

    // Close drawer callback
    const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

    // Escape key closes drawer
    useEffect(() => {
        if (!isDrawerOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeDrawer();
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isDrawerOpen, closeDrawer]);

    // Body scroll lock when drawer is open
    useEffect(() => {
        if (isDrawerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isDrawerOpen]);

    // Merge with default config — matches the /start page nav:
    // Left: logo | Right: tagline + hamburger
    const finalHeaderConfig: HeaderConfig = {
        position: 'fixed',
        background: 'black',
        textColor: 'light',
        scrollBehavior: 'always',
        elevation: 'none',
        // Layout defaults — centered logo (default public nav); pages that want logo-left set logoAlignment:'left'
        logoAlignment: 'center',
        menuAlignment: 'right',
        showMenu: true,
        // Primary CTA hidden by default
        showCta: false,
        ctaText: '',
        ctaAction: 'navigate',
        ctaUrl: '',
        ctaStyle: 'filled',
        // Secondary CTA hidden by default
        showSecondaryCta: false,
        secondaryCtaText: '',
        secondaryCtaUrl: '',
        // No sub-navigation bar
        showNavBar: false,
        navMenuLocation: 'header_nav',
        // Tagline (intentionally empty by default — agent pages override with 'Luxury Travel Advisor' singular).
        tagline: '',
        ...headerConfig,
    };

    // Main nav is always sticky for scroll-aware polish — pages that previously used
    // position:'absolute' (overlay) now get sticky in-flow behavior by default.
    const hasStickyHeader = finalHeaderConfig.position !== 'relative';

    // Drawer context provides open/close handlers
    const drawerContextValue: DrawerContextType = {
        isOpen: isDrawerOpen,
        setIsOpen: setIsDrawerOpen,
    };

    return (
        <UserTypeContext.Provider value={{ userType, setUserType }}>
            <HeaderConfigContext.Provider value={{ headerConfig: finalHeaderConfig }}>
                <DrawerContext.Provider value={drawerContextValue}>
                    {/* Root container - scrollbar always at viewport right edge */}
                    <div className="relative h-[100dvh] overflow-y-auto bg-[#0b2222]" style={{ overflowX: 'clip' }}>
                        {/* Drawer - fixed to viewport, above everything */}
                        <nav
                            ref={drawerRef}
                            id="main-drawer"
                            role="dialog"
                            aria-modal="true"
                            aria-label="Site navigation"
                            aria-hidden={!isDrawerOpen}
                            className={cn(
                                'fixed top-0 right-0 z-[60] flex h-[100dvh] w-[var(--drawer-width)] flex-col bg-[#0b2222]',
                                'transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]',
                                isDrawerOpen ? 'translate-x-0' : 'translate-x-full',
                            )}
                        >
                            {/* Decorative hairline rule */}
                            <div className="h-px bg-[#F5F5F5]/10" />

                            {/* Close button — always visible inside the drawer */}
                            <button
                                type="button"
                                onClick={closeDrawer}
                                aria-label="Close navigation"
                                className="absolute top-0 right-0 flex h-16 w-16 items-center justify-center text-[#F5F5F5]/50 transition-colors hover:text-[#F5F5F5] focus-visible:ring-2 focus-visible:ring-[#F5F5F5]/50 focus-visible:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>

                            <motion.div
                                variants={drawerMenuVariants}
                                initial="hidden"
                                animate={isDrawerOpen ? 'visible' : 'hidden'}
                                className="flex flex-1 flex-col justify-center sm:justify-start"
                            >
                                {/* Brand mark — only on the full-width drawer (<sm), anchors the centered cluster */}
                                <motion.div variants={drawerItemVariants} onClick={closeDrawer} className="mb-8 flex justify-center sm:hidden">
                                    <Link href="/" aria-label="LuxRally Travel — Home" className="inline-flex opacity-70 transition-opacity hover:opacity-100">
                                        <LRTIcon className="h-12 w-auto" />
                                    </Link>
                                </motion.div>

                                <MenuRenderer
                                    location="drawer"
                                    className="nav-px flex flex-col sm:flex-1 sm:pt-20"
                                    itemClassName="py-4 lux-caps lux-caps-sm justify-center sm:justify-start text-[#F5F5F5] transition-all duration-300 hover:tracking-[0.28em] hover:text-[#F5F5F5]"
                                    renderItem={(item, defaultRender) => (
                                        <motion.div key={item.id} variants={drawerItemVariants} onClick={closeDrawer}>
                                            {defaultRender()}
                                        </motion.div>
                                    )}
                                />

                                {/* Search — minimal, styled like other drawer items */}
                                <motion.div variants={drawerItemVariants} onClick={closeDrawer} className="mt-12 sm:mt-0">
                                    <Link
                                        href="/search"
                                        className="nav-px lux-caps lux-caps-sm flex items-center justify-center gap-3 py-4 text-[#F5F5F5] transition-all duration-300 hover:tracking-[0.28em] sm:justify-start"
                                    >
                                        <Search className="h-3.5 w-3.5" strokeWidth={1.5} />
                                        <span>Search</span>
                                    </Link>
                                </motion.div>
                            </motion.div>

                            {/* Hairline above socials footer */}
                            <div className="nav-px">
                                <div className="h-px bg-[#F5F5F5]/10" />
                            </div>

                            {/* Socials */}
                            <motion.div
                                variants={drawerSocialVariants}
                                initial="hidden"
                                animate={isDrawerOpen ? 'visible' : 'hidden'}
                                className="nav-px py-8 text-center sm:text-left"
                            >
                                <span className="lux-caps lux-caps-sm mb-4 block text-[#F5F5F5]/40">Follow</span>
                                <SocialMenuRenderer
                                    location="social_drawer"
                                    className="flex justify-center gap-4 sm:justify-start"
                                    iconSize="h-5 w-5"
                                    iconClassName="text-[#F5F5F5]/60 transition-colors hover:text-[#F5F5F5]"
                                />
                            </motion.div>
                        </nav>

                        {/* Wrapper - contains header + content, shifts left when drawer opens */}
                        <div
                            className={cn(
                                'relative z-50 flex min-h-[100dvh] w-screen flex-col bg-[#0e2727] text-[#F5F5F5]',
                                'transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]',
                            )}
                            style={{
                                transform: isDrawerOpen ? 'translateX(calc(var(--drawer-width) * -1))' : undefined,
                            }}
                        >
                            {/* Backdrop overlay */}
                            <AnimatePresence>
                                {isDrawerOpen && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.4 }}
                                        className="fixed inset-0 z-[55] bg-black/50"
                                        aria-hidden="true"
                                        onClick={closeDrawer}
                                    />
                                )}
                            </AnimatePresence>

                            {/* Header - sticky/fixed within this wrapper */}
                            {!finalHeaderConfig.hideHeader && (
                                <div
                                    className={cn(
                                        hasStickyHeader && 'sticky top-0 z-20',
                                        !hasStickyHeader && 'absolute top-0 right-0 left-0 z-20',
                                        finalHeaderConfig.hideHeaderMobile && 'hidden md:block',
                                    )}
                                >
                                    <PublicHeader />
                                </div>
                            )}

                            {/* Content — `min-h-[100dvh]` reserves a full viewport even before
                                children hydrate, so the footer doesn't initially render right under
                                the header and then jump down when blocks load (the dominant CLS
                                contributor on the homepage, 0.84 in measured shifts). The outer
                                container already has min-h-[100dvh], but on initial paint flex-1
                                doesn't always expand main before content arrives. */}
                            <main className="relative flex-1 min-h-[100dvh]">{children}</main>

                            <PublicFooter />
                        </div>
                    </div>
                </DrawerContext.Provider>
            </HeaderConfigContext.Provider>
        </UserTypeContext.Provider>
    );
}
